// MatchBoardPage.tsx — the buyers' two-sided billing worklist (chunk 2, phase 6).
//
// Odoo's bill-matching screen is the reference shape: what ordering still
// expects to be billed on the left, invoice lines that name no order on the
// right, and one act — pick a row on each side, link them. The link delegates
// to the same writer the financial review screen uses, so every guard answers
// identically; confirming (the money act) stays on the financial side.
//
// The shared table hosts both piles now — two independent DataTables, one per
// pane, each with its own module-scope columns. What it does NOT host is the
// picking: selection is click-per-side rather than the queue's J/K row dialect,
// and it stays the page's, traveling to the rows by `rowProps`. A two-pane
// keyboard needs a pane-focus vocabulary the component does not have yet, so
// `rowKeyboard` is deliberately absent on both sides — passing it would put a
// second, component-owned selection on each pane, competing with the pick the
// action bar reads. When the component grows a pane-focus dialect, this page is
// the consumer-in-waiting.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useToast } from '../components/Toast/ToastContext';
import { formatEventDate } from '../utils/date-utils';
import PageHeader from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Appear } from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { EmptyState } from '../components/ui/EmptyState';
import { LogoSpinner } from '../components/ui/LogoSpinner';
import { Tag } from '../components/ui/Tag';
import { Tooltip } from '../components/ui/Tooltip';
import { FilterBar } from '../components/FilterBar';
import { SegTrack } from '../components/ui/SegTrack';
import { DataTable } from '../components/table/DataTable';
// Name-first. No SKU on this board: it is an internal worklist, and the buyer
// matching a bill reads the item's name, not its code.
import ItemName from '../components/ItemName';
// This module's own formatter, NOT AccountsPayable's: the Procurement pages
// hold a no-comma convention (web/CLAUDE.md), and the same line must not read
// differently here than on the PO detail one click away.
import { formatCents } from './poApi';
import { useMatchBoardApi, BoardPoLine, BoardInvoiceLine } from './matchBoardApi';

const PILL_TONES = { gold: 'warn', red: 'bad', blue: 'info', gray: 'neutral' } as const;

const Pill = ({ tone, children }: { tone: keyof typeof PILL_TONES; children: React.ReactNode }) => (
  <Tag size="sm" tone={PILL_TONES[tone]} className="mr-1 mb-0.5">
    {children}
  </Tag>
);

// The picked row's treatment, page-side because the pick is page-side. Hover
// and the row's base transition belong to the vessel now, so only the picked
// branch is said here, and it is said in the vessel's OWN selection tone. Not a
// rename for its own sake: the gray this used to name resolves to #3a3a3a
// (tailwind.config.js), the same hex --surface-container-highest holds, and the
// flat token is the tone the vessel already hovers to — so a picked row does
// not repaint under the pointer, which is exactly how this composed when hover
// and picked were the two branches of one ternary. The inset accent ring is
// unchanged, and it is what carries "picked" either way.
const pickedRowClass = (picked: boolean) =>
  `cursor-pointer${picked ? ' bg-surface-container-highest shadow-[inset_3px_0_0_0_var(--color-primary,#ffcc33)]' : ''}`;

const invoiceVendor = (l: BoardInvoiceLine) => l.vendor_name || l.vendor_observed || 'Unknown vendor';

// Which invoices are converging on a line. The numbers list is null-filtered
// server-side, so a count above its length means an UNNUMBERED invoice is in
// the pile — say so rather than undercount the convergence.
const stagedSources = (row: BoardPoLine): string => {
  const named = row.pending_invoice_numbers;
  if (!named.length) return 'an unnumbered invoice';
  if (row.pending_invoice_count > named.length) return `${named.join(', ')}, and an unnumbered invoice`;
  return named.join(', ');
};

// Two column arrays, module-scope per the DataTable contract: every cell is
// pure over its own row, and neither side sorts — the server's order is the
// board's order on both. Right alignment is meta.align, never a class, and the
// money type treatment stays on the inner elements because each of these cells
// stacks two figures at two weights.
const PO_COLUMNS: ColumnDef<BoardPoLine, any>[] = [
  {
    id: 'order',
    header: 'Order',
    cell: ({ row }) => (
      <>
        <div className="font-mono text-[13px] text-on-surface">
          {row.original.po_number} · line {row.original.line_number}
          {row.original.po_status === 'closed' && <span className="ml-1.5"><Pill tone="gray">order closed</Pill></span>}
        </div>
        <div className="text-[11px] text-on-surface-variant">{row.original.vendor_name || 'Unknown vendor'}</div>
      </>
    ),
  },
  {
    id: 'part',
    header: 'Part',
    cell: ({ row }) => <ItemName size="sm" item={row.original} className="max-w-[220px]" />,
  },
  {
    id: 'remaining',
    header: 'Remaining',
    meta: { align: 'right' },
    cell: ({ row }) => (
      <>
        <div className="font-mono tabular-nums text-on-surface">
          {formatCents(row.original.remaining_to_bill_cents)}
        </div>
        <div className="font-mono tabular-nums text-[11px] text-on-surface-variant">
          {formatCents(row.original.billed_amount_cents)} billed of {formatCents(row.original.line_value_cents)}
        </div>
        {row.original.pending_invoice_count > 0 && (
          <Tooltip label={`Staged on: ${stagedSources(row.original)}`}>
          <div className="mt-0.5">
            {row.original.over_pending ? (
              <Pill tone="red">
                {formatCents(row.original.pending_cents)} staged, past this line's value
              </Pill>
            ) : row.original.pending_cents !== 0 ? (
              <Pill tone="gold">{formatCents(row.original.pending_cents)} staged in review</Pill>
            ) : (
              <Pill tone="gold">staged in review, amount unreadable</Pill>
            )}
          </div>
          </Tooltip>
        )}
      </>
    ),
  },
];

const INVOICE_COLUMNS: ColumnDef<BoardInvoiceLine, any>[] = [
  {
    id: 'invoice',
    header: 'Invoice',
    cell: ({ row }) => (
      <>
        <div className="font-mono text-[13px] text-on-surface">
          {row.original.invoice_number || '—'}
          {row.original.is_credit_memo && <Pill tone="blue">credit memo</Pill>}
        </div>
        <div className="text-[11px] text-on-surface-variant">
          {invoiceVendor(row.original)}
          {row.original.invoice_date ? ` · ${formatEventDate(row.original.invoice_date)}` : ''}
        </div>
      </>
    ),
  },
  {
    id: 'line',
    header: 'Line',
    cell: ({ row }) => (
      <>
        <div className="text-on-surface truncate max-w-[220px]">{row.original.description || '—'}</div>
        <div className="font-mono text-[11px] text-on-surface-variant">
          {row.original.part_number_observed || row.original.mfg_part_number_observed || ''}
          {row.original.line_type === 'charge' && <Pill tone="gray">{row.original.charge_type}</Pill>}
        </div>
      </>
    ),
  },
  {
    id: 'amount',
    header: 'Amount',
    meta: { align: 'right' },
    cell: ({ row }) => (
      <span className={`font-mono tabular-nums ${Number(row.original.amount_cents) < 0 ? 'text-sky-300' : 'text-on-surface'}`}>
        {formatCents(row.original.amount_cents)}
      </span>
    ),
  },
];

const poRowId = (r: BoardPoLine) => r.id;
const invoiceRowId = (r: BoardInvoiceLine) => r.id;

export default function MatchBoardPage() {
  const api = useMatchBoardApi();
  const toast = useToast();

  const [poLines, setPoLines] = useState<BoardPoLine[]>([]);
  const [invoiceLines, setInvoiceLines] = useState<BoardInvoiceLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState<string | null>(null);
  const [pickedPo, setPickedPo] = useState<string | null>(null);
  const [pickedInvoice, setPickedInvoice] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const board = await api.getBoard();
      setPoLines(board.po_lines);
      setInvoiceLines(board.invoice_lines);
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'The board could not be loaded.');
    } finally {
      setLoading(false);
    }
    // api/toast are stable context handles; re-running on their identity would refetch every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // The vendor chips come from whoever is actually ON the board, both sides.
  const vendors = useMemo(() => {
    const names = new Set<string>();
    poLines.forEach((r) => names.add(r.vendor_name || 'Unknown vendor'));
    invoiceLines.forEach((r) => names.add(invoiceVendor(r)));
    return Array.from(names).sort();
  }, [poLines, invoiceLines]);

  const visiblePo = useMemo(
    () => (vendor ? poLines.filter((r) => (r.vendor_name || 'Unknown vendor') === vendor) : poLines),
    [poLines, vendor],
  );
  const visibleInvoice = useMemo(
    () => (vendor ? invoiceLines.filter((r) => invoiceVendor(r) === vendor) : invoiceLines),
    [invoiceLines, vendor],
  );

  // A pick the filter has hidden is a pick you can no longer see: drop it
  // rather than let the action bar link a row that is not on screen.
  useEffect(() => {
    if (pickedPo && !visiblePo.some((r) => r.id === pickedPo)) setPickedPo(null);
    if (pickedInvoice && !visibleInvoice.some((r) => r.id === pickedInvoice)) setPickedInvoice(null);
  }, [visiblePo, visibleInvoice, pickedPo, pickedInvoice]);

  const poPick = poLines.find((r) => r.id === pickedPo) || null;
  const invoicePick = invoiceLines.find((r) => r.id === pickedInvoice) || null;

  // The server refuses a cross-vendor link; say so before the round trip.
  // An unknown-vendor invoice may link anywhere — same rule as the server's.
  const vendorsClash = Boolean(
    poPick && invoicePick && poPick.vendor_id && invoicePick.vendor_id
    && poPick.vendor_id !== invoicePick.vendor_id,
  );

  const onLink = async () => {
    if (!poPick || !invoicePick) return;
    setLinking(true);
    try {
      await api.link(invoicePick.id, poPick.id);
      toast.success(`Linked to ${poPick.po_number} line ${poPick.line_number}. Confirming the invoice is what bills it.`);
      setPickedPo(null);
      setPickedInvoice(null);
      await load();
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'The link was refused.');
    } finally {
      setLinking(false);
    }
  };

  return (
    <PageLayout width="full" kind="workspace" className="pt-4 lg:pt-6 space-y-4">
      {/* No `primary`: the page's one act is Link the pair, and it lives in the
          sticky bar at the bottom beside the two picks it acts on. A filled pill
          up here would be a second loud thing, pointing at nothing yet. */}
      <PageHeader
        title="Bill matching"
        helpTitle="About bill matching"
        lede="What your orders still expect to be billed, beside invoice lines that name no order. Pick one row on each side to pair them."
        help={
          <>
            <p>
              The left side is what your orders are still waiting to be billed for. The right
              side is invoice lines that arrived naming no order. Pick one row on each side and
              link them; the invoice then carries the answer when accounting confirms it.
            </p>
            <p className="text-on-surface font-medium">Linking moves no money</p>
            <p>
              A link is a note on the invoice, not a payment. The money lands on the order only
              when accounting confirms the invoice, and that happens on their side.
            </p>
            <p className="text-on-surface font-medium">Staged in review</p>
            <p>
              A gold tag means an invoice in review already points at this line. A red tag means
              the invoices pointing at it add up to more than the line is worth: that is how a
              double bill looks before it happens, so check those invoices before anyone confirms.
            </p>
            <p className="text-on-surface font-medium">Closed orders</p>
            <p>
              A closed order stops waiting for billing, so its lines leave this board. It comes
              back only if an invoice in review points at it, wearing an "order closed" tag,
              because a late invoice against a finished order deserves a look.
            </p>
            <p className="text-on-surface font-medium">Lines that never leave</p>
            <p>
              A line stays on the left until its full value is billed. If a vendor legitimately
              billed less (freight rolled elsewhere, a rounding difference), closing the order is
              what retires it today.
            </p>
          </>
        }
      />

      {/* The one axis that narrows this board, so it is the one thing in the
          bar. "Everyone" is the empty key rather than a fifth state: `vendor`
          stays null in page state, and a segmented control needs a string. */}
      <Reveal show={vendors.length > 1}>
        <FilterBar>
          <SegTrack
            label="Which vendor to show"
            wrap
            value={vendor ?? ''}
            onChange={(key) => setVendor(key || null)}
            options={[
              { key: '', label: 'Everyone' },
              ...vendors.map((v) => ({ key: v, label: v })),
            ]}
          />
        </FilterBar>
      </Reveal>

      {loading ? (
        <FadeIn key="loading"><LogoSpinner label="Loading the board" /></FadeIn>
      ) : (
        <FadeIn key="board" className="grid gap-4 lg:grid-cols-2 items-start">
          {/* ── PO side: commitments still owed billing ───────────────────
              Each pane's title rides in its own vessel's strip, so a heading
              names the pile it sits on rather than floating above it — and
              with two vessels side by side, each one says which side it is.
              emptyMode 'replace' keeps this board's rule that an empty side
              shows no column headers over nothing. */}
          <section>
            <DataTable<BoardPoLine>
              data={visiblePo}
              columns={PO_COLUMNS}
              getRowId={poRowId}
              strip={
                <h2 className="font-mono text-[11px] uppercase tracking-widest text-on-surface-variant">
                  Waiting to be billed
                </h2>
              }
              emptyMode="replace"
              empty={
                <EmptyState variant="area" title="Nothing is waiting to be billed.">
                  Every open order line is fully billed.
                </EmptyState>
              }
              rowProps={(row) => ({
                'data-testid': `po-row-${row.id}`,
                onClick: () => setPickedPo(pickedPo === row.id ? null : row.id),
                className: pickedRowClass(pickedPo === row.id),
              })}
            />
          </section>

          {/* ── Invoice side: paper nothing explains ────────────────────── */}
          <section>
            <DataTable<BoardInvoiceLine>
              data={visibleInvoice}
              columns={INVOICE_COLUMNS}
              getRowId={invoiceRowId}
              strip={
                <h2 className="font-mono text-[11px] uppercase tracking-widest text-on-surface-variant">
                  Invoice lines with no order
                </h2>
              }
              emptyMode="replace"
              empty={
                <EmptyState variant="area" title="Every invoice line names its order.">
                  New invoices land in the review queue first; their unmatched lines appear here.
                </EmptyState>
              }
              rowProps={(row) => ({
                'data-testid': `invoice-line-row-${row.id}`,
                onClick: () => setPickedInvoice(pickedInvoice === row.id ? null : row.id),
                className: pickedRowClass(pickedInvoice === row.id),
              })}
            />
          </section>
        </FadeIn>
      )}

      {/* ── The one act: pair the two picks ─────────────────────────────── */}
      <Appear as="div" show={!!(poPick || invoicePick)} className="sticky bottom-4 mt-4 bg-surface-container-high rounded-lg px-4 py-3 shadow-lg flex items-center gap-3 flex-wrap">
          <div className="text-sm text-on-surface flex-1 min-w-[200px]">
            {invoicePick
              ? <span className="font-mono text-[13px]">{invoicePick.invoice_number} line {invoicePick.line_number}</span>
              : <span className="text-on-surface-variant">Pick an invoice line</span>}
            <span className="text-on-surface-variant mx-2">to</span>
            {poPick
              ? <span className="font-mono text-[13px]">{poPick.po_number} line {poPick.line_number}</span>
              : <span className="text-on-surface-variant">pick an order line</span>}
            <Alert show={vendorsClash} tone="error" variant="text" className="mt-1 !text-xs !leading-4">
              Those belong to different vendors, so they cannot be linked.
            </Alert>
          </div>
          <Button
            variant="quiet"
            onClick={() => { setPickedPo(null); setPickedInvoice(null); }}
          >
            Clear
          </Button>
          <Button
            variant="filled"
            onClick={onLink}
            disabled={!poPick || !invoicePick || vendorsClash || linking}
          >
            {linking ? 'Linking…' : 'Link the pair'}
          </Button>
      </Appear>
    </PageLayout>
  );
}
